/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.PAGE.MASTER
 *
 * Hierarchical menu maintenance with drag-and-drop reordering and reparenting.
 *  - Toolbar: persistent search filter, New Root / New Child, Refresh.
 *  - Single full-width grid with hierarchy shown by indentation +
 *    expand/collapse glyphs.
 *  - HTML5 native drag-and-drop. Three drop zones per row:
 *      top 25% : drop ABOVE  (insert as preceding sibling)
 *      bot 25% : drop BELOW  (insert as following sibling)
 *      mid 50% : drop INTO   (reparent as child)
 *  - Cycle prevention: a node cannot be dropped onto itself or any descendant.
 *  - Drag-and-drop is disabled while a search filter is active because the
 *    visual order doesn't match the persisted tree.
 *  - Edit drawer slides in from the right (no Sort Order field - drag drives it).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveLocale } from '../../lib/use_active_locale';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { apiDb } from '../../lib/api_client';
import { fetchAllRows } from '../../lib/fetch_all_rows';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import type { AuthSession } from '../../types/auth';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { Modal } from '../../components/common/Modal';
import { ConfirmModal } from '../../components/common/ConfirmModal';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { RowActionsMenu } from '../../components/common/RowActionsMenu';
import { useDirtyForm } from '../../hooks/use_dirty_form';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { RelatedChildrenPanel, RelatedExpandButton, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';
import { registerChildEditor, type ChildEditorProps } from '../../lib/child_editors';
import { useRecordExtensions } from '../../hooks/use_record_extensions';
import { useRecordExtensionForm } from '../../hooks/use_record_extension_form';
import { ExtensionFacets } from '../../components/common/ExtensionFacets';
import { RecordExtensionFields } from '../../components/common/RecordExtensionFields';

const PAGE_CODE = 'CSM.PAGE.MASTER';

const PAGE_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code',    type: 'text',    label: 'Internal Code', required: true, col_span: 1 },
  { key: 'route_path',       type: 'text',    label: 'Route Path',    col_span: 1 },
  { key: 'application',      type: 'custom',  label: 'Application',   col_span: 1 },
  { key: 'parent_page',      type: 'custom',  label: 'Parent',        col_span: 1 },
  { key: 'is_active',        type: 'boolean', label: 'Active',        col_span: 1 },
  { key: 'requires_signoff', type: 'boolean', label: 'Requires Sign-off', col_span: 1 },
  { key: 'signoff_count',    type: 'number',  label: 'Sign-off Count', col_span: 1 },
];
const PAGE_CORE_LAYOUT = toUldCoreLayout(PAGE_FIELD_DEFS);

type PageSortKey =
  | 'label' | 'internal_code' | 'route_path' | 'application_label'
  | 'parent_label' | 'is_active' | 'is_user_managed';

interface PageRow {
  page_id: number;
  internal_code: string;
  route_path: string;
  parent_page_id: number | null;
  application_id: number | null;
  sort_order: number;
  requires_signoff: 'Y' | 'N';
  signoff_count: number;
  is_active: 'Y' | 'N';
  is_user_managed: 'Y' | 'N';
}

/** A selectable application (csm_application_master) with its active-locale label. */
interface AppOption {
  application_id: number;
  internal_code: string;
  is_default: 'Y' | 'N';
  label: string;
}

interface TLRow {
  locale_code: string;
  display_text: string;
  description: string;
}

interface PageNode extends PageRow {
  label: string;
  children: PageNode[];
}

interface FlatRow {
  node: PageNode;
  depth: number;
  parentLabel: string;
}

/**
 * Expand/contract "related children" model (prototype).
 * A page (an "action") is a parent that can have several kinds of children:
 *  - the roles granted access to it (csm_role_page_access),
 *  - the job descriptions ("duties") that reach it transitively via job->role,
 *  - its sub-pages (the parent_page_id hierarchy).
 * The detail panel renders each non-empty collection as a side-by-side card.
 */
interface RoleAccess {
  role_id: number;
  code: string;
  name: string;
  is_active: 'Y' | 'N';
  perms: string[];   // compact V/A/C/D/S summary
}
interface DutyRef {
  job_id: number;
  code: string;
  name: string;
}

/** Permission-badge hover labels for the Roles-with-access card. */
const PERM_TITLES: Record<string, string> = { V: 'View', A: 'Add', C: 'Change', D: 'Delete', S: 'Sign-off' };

type DropPosition = 'above' | 'below' | 'into';

interface DropTarget {
  page_id: number;
  position: DropPosition;
}

const FILTER_KEY = 'csm.pages.filter';
const EXPAND_KEY = 'csm.page-master.expanded';
const SORT_STEP  = 10;   // gap between sibling sort_order values

interface DraftCreate { parent_id: number | null; }

const GRID_COLS: GridColumnDef[] = [
  { key: 'label',          header: 'Page',        defaultWidth: 220 },
  { key: 'internal_code',  header: 'Code',        defaultWidth: 180 },
  { key: 'route_path',     header: 'Route',       defaultWidth: 180 },
  { key: 'application_label', header: 'Application', defaultWidth: 160 },
  { key: 'parent_label',   header: 'Parent',      defaultWidth: 160 },
  { key: 'is_active',      header: 'Active',      defaultWidth: 80  },
  { key: 'is_user_managed',header: 'Source',      defaultWidth: 80  },
];

export interface PageMasterPageProps {
  session: AuthSession;
}

export function PageMasterPage({ session }: PageMasterPageProps) {
  return (
    <PersonalizationProvider pageCode="CSM.PAGE.MASTER" session={session}>
      <PageMasterPageInner session={session} />
    </PersonalizationProvider>
  );
}

function PageMasterPageInner({ session }: PageMasterPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const [rows, setRows]         = useState<PageRow[]>([]);
  const [labels, setLabels]     = useState<Record<number, string>>({});
  // The resolved `labels` map above collapses each page to ONE string; §17.B14
  // needs every locale, so keep the raw rows too.
  const [tlByPage, setTlByPage] = useState<Record<number, { locale_code: string; display_text: string }[]>>({});
  const [apps, setApps]         = useState<AppOption[]>([]);
  // Related-children (expand/contract detail panel) - keyed by page_id.
  const [rolesByPage, setRolesByPage]   = useState<Map<number, RoleAccess[]>>(new Map());
  const [dutiesByPage, setDutiesByPage] = useState<Map<number, DutyRef[]>>(new Map());
  const [detailExpanded, setDetailExpanded] = useState<Set<number>>(new Set());
  const [filter, setFilter]     = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B12: per-column multi-value facets. Active defaults to Active-only on load;
  // Application is neutral (all) by default. Application values are stringified ids.
  const [facets, setFacets]     = useState<{ active: string[]; application: string[] }>({ active: ['Y'], application: [] });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draft, setDraft]       = useState<DraftCreate | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem(EXPAND_KEY);
      return raw ? new Set<number>(JSON.parse(raw)) : new Set();
    } catch { return new Set(); }
  });
  const [deleteTarget, setDeleteTarget] = useState<PageRow | null>(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sort = useSort<PageSortKey>('csm.pages.sort', { key: null, dir: 'asc' });

  // DnD state. Refs hold the live values for drag handlers (state would lag).
  const draggingIdRef = useRef<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);
  useEffect(() => {
    try { localStorage.setItem(EXPAND_KEY, JSON.stringify([...expanded])); } catch { /* noop */ }
  }, [expanded]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const locale = activeLocale;
      // The page catalog is a TREE that must build its full hierarchy, and it can
      // exceed PostgREST's row cap (thousands of app-scoped "action" rows), so we
      // range-page the WHOLE set rather than silently stopping at the cap. sort_order
      // is not unique (all action rows share 0), so page_id is the stable tiebreaker.
      type RawPage = PageRow & { csm_page_master_tl?: { locale_code: string; display_text: string }[] };
      const data = await fetchAllRows<RawPage>((from, to) => apiDb
        .from('csm_page_master')
        .select(`
          page_id, internal_code, route_path, parent_page_id, application_id, sort_order,
          requires_signoff, signoff_count, is_active, is_user_managed,
          csm_page_master_tl ( locale_code, display_text )
        `)
        .order('sort_order').order('page_id').range(from, to));

      const pages: PageRow[] = data.map((p: RawPage) => ({
        page_id: p.page_id,
        internal_code: p.internal_code,
        // route_path is null for non-navigable rows (NAV groups, other-app action
        // rows); normalize so the PageRow.route_path:string invariant holds and the
        // filter/sort/render never hit null (matches fix on the null-route branch).
        route_path: p.route_path ?? '',
        parent_page_id: p.parent_page_id,
        application_id: p.application_id,
        sort_order: p.sort_order,
        requires_signoff: p.requires_signoff,
        signoff_count: p.signoff_count,
        is_active: p.is_active,
        is_user_managed: p.is_user_managed,
      }));

      // Applications (csm_application_master) — resolved to their active-locale
      // label so the grid, facet and modal all show names, never raw ids (§17.B4).
      const { data: appData } = await apiDb
        .from('csm_application_master')
        .select('application_id, internal_code, is_default, csm_application_master_tl ( locale_code, display_text )')
        .order('internal_code');
      type RawApp = { application_id: number; internal_code: string; is_default: 'Y' | 'N'; csm_application_master_tl?: { locale_code: string; display_text: string }[] };
      const appList: AppOption[] = (appData ?? []).map((a: RawApp) => {
        const tls = a.csm_application_master_tl ?? [];
        const label = tls.find(x => x.locale_code === locale)?.display_text
          ?? tls.find(x => x.locale_code === 'EN_US')?.display_text
          ?? a.internal_code;
        return { application_id: a.application_id, internal_code: a.internal_code, is_default: a.is_default, label };
      });
      setApps(appList);

      const lab: Record<number, string> = {};
      const tlMap: Record<number, { locale_code: string; display_text: string }[]> = {};
      for (const p of (data ?? []) as RawPage[]) {
        const tls: { locale_code: string; display_text: string }[] = p.csm_page_master_tl ?? [];
        const exact = tls.find(t => t.locale_code === locale);
        const fallback = tls.find(t => t.locale_code === 'EN_US') ?? tls[0];
        lab[p.page_id] = exact?.display_text ?? fallback?.display_text ?? p.internal_code;
        tlMap[p.page_id] = tls;
      }
      setRows(pages);
      setLabels(lab);
      setTlByPage(tlMap);

      // ── Related children for the expand/contract detail panel ───────────────
      // roles with access (csm_role_page_access) + the job descriptions
      // ("duties") that reach each page transitively via job -> role.
      const [rolesRes, jobsRes, jdrRes, rpaRes] = await Promise.all([
        apiDb.from('csm_role_master').select('role_id, internal_code, is_active, csm_role_master_tl ( locale_code, display_text )'),
        apiDb.from('csm_job_description').select('job_description_id, internal_code, csm_job_description_tl ( locale_code, display_text )'),
        apiDb.from('csm_job_description_role').select('job_description_id, role_id'),
        apiDb.from('csm_role_page_access').select('page_id, role_id, can_view, can_add, can_change, can_delete, can_signoff'),
      ]);

      type Tl = { locale_code: string; display_text: string };
      const pickTl = (tls: Tl[] | undefined, code: string) => {
        const list = tls ?? [];
        return list.find(x => x.locale_code === locale)?.display_text
          ?? list.find(x => x.locale_code === 'EN_US')?.display_text
          ?? list[0]?.display_text ?? code;
      };

      type RawRole = { role_id: number; internal_code: string; is_active: 'Y' | 'N'; csm_role_master_tl?: Tl[] };
      type RawJob  = { job_description_id: number; internal_code: string; csm_job_description_tl?: Tl[] };
      const roleInfo = new Map<number, { code: string; name: string; is_active: 'Y' | 'N' }>();
      for (const r of (rolesRes.data ?? []) as RawRole[]) {
        roleInfo.set(r.role_id, { code: r.internal_code, name: pickTl(r.csm_role_master_tl, r.internal_code), is_active: r.is_active });
      }
      const jobInfo = new Map<number, { code: string; name: string }>();
      for (const j of (jobsRes.data ?? []) as RawJob[]) {
        jobInfo.set(j.job_description_id, { code: j.internal_code, name: pickTl(j.csm_job_description_tl, j.internal_code) });
      }
      const roleToJobs = new Map<number, Set<number>>();
      for (const jr of (jdrRes.data ?? []) as { job_description_id: number; role_id: number }[]) {
        const set = roleToJobs.get(jr.role_id) ?? new Set<number>();
        set.add(jr.job_description_id);
        roleToJobs.set(jr.role_id, set);
      }

      type RawRpa = { page_id: number; role_id: number; can_view: boolean; can_add: boolean; can_change: boolean; can_delete: boolean; can_signoff: boolean };
      const roleMap = new Map<number, RoleAccess[]>();
      const dutyMap = new Map<number, DutyRef[]>();
      const dutySeen = new Map<number, Set<number>>();
      for (const a of (rpaRes.data ?? []) as RawRpa[]) {
        const info = roleInfo.get(a.role_id);
        if (!info) continue;
        const perms: string[] = [];
        if (a.can_view) perms.push('V');
        if (a.can_add) perms.push('A');
        if (a.can_change) perms.push('C');
        if (a.can_delete) perms.push('D');
        if (a.can_signoff) perms.push('S');
        const arr = roleMap.get(a.page_id) ?? [];
        arr.push({ role_id: a.role_id, code: info.code, name: info.name, is_active: info.is_active, perms });
        roleMap.set(a.page_id, arr);

        const jobs = roleToJobs.get(a.role_id);
        if (jobs) {
          const dArr = dutyMap.get(a.page_id) ?? [];
          const seen = dutySeen.get(a.page_id) ?? new Set<number>();
          for (const jid of jobs) {
            if (seen.has(jid)) continue;
            seen.add(jid);
            const ji = jobInfo.get(jid);
            if (ji) dArr.push({ job_id: jid, code: ji.code, name: ji.name });
          }
          dutyMap.set(a.page_id, dArr);
          dutySeen.set(a.page_id, seen);
        }
      }
      for (const list of roleMap.values()) list.sort((x, y) => x.name.localeCompare(y.name));
      for (const list of dutyMap.values()) list.sort((x, y) => x.name.localeCompare(y.name));
      setRolesByPage(roleMap);
      setDutiesByPage(dutyMap);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; re-resolve _tl display on locale switch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload();   }, [activeLocale]);

  // A derived app's own page attributes, declared as a manifest against this page
  // (src/app/extensions). Inert — no query, no facet — when none is registered.
  const ext = useRecordExtensions(PAGE_CODE, { pkColumn: 'page_id' });

  const tree = useMemo<PageNode[]>(() => {
    const byId = new Map<number, PageNode>();
    for (const r of rows) {
      byId.set(r.page_id, { ...r, label: labels[r.page_id] ?? r.internal_code, children: [] });
    }
    const roots: PageNode[] = [];
    for (const node of byId.values()) {
      const parent = node.parent_page_id != null ? byId.get(node.parent_page_id) : undefined;
      if (parent) parent.children.push(node);
      else roots.push(node);
    }
    const sortFn = (a: PageNode, b: PageNode) =>
      (a.sort_order - b.sort_order) || a.label.localeCompare(b.label);
    const walk = (nodes: PageNode[]) => { nodes.sort(sortFn); nodes.forEach(n => walk(n.children)); };
    walk(roots);
    return roots;
  }, [rows, labels]);

  // Transitive RBAC usage per page: the roles that can access the page OR any page
  // beneath it (permissions unioned), plus the job descriptions that reach it — so a
  // leaf shows its own roles & permissions and a menu group rolls up the roles &
  // permissions that apply across the pages it contains. This is "where each page is
  // used" in access terms, not a repeat of the menu hierarchy.
  const relatedByPage = useMemo(() => {
    const permOrder = ['V', 'A', 'C', 'D', 'S'];
    const roles = new Map<number, RoleAccess[]>();
    const duties = new Map<number, DutyRef[]>();
    const visit = (node: PageNode): { r: Map<number, RoleAccess>; d: Map<number, DutyRef> } => {
      const rMap = new Map<number, RoleAccess>();
      const dMap = new Map<number, DutyRef>();
      for (const ra of rolesByPage.get(node.page_id) ?? []) rMap.set(ra.role_id, { ...ra, perms: [...ra.perms] });
      for (const du of dutiesByPage.get(node.page_id) ?? []) if (!dMap.has(du.job_id)) dMap.set(du.job_id, du);
      for (const child of node.children) {
        const sub = visit(child);
        for (const [rid, ra] of sub.r) {
          const cur = rMap.get(rid);
          if (!cur) rMap.set(rid, { ...ra, perms: [...ra.perms] });
          else for (const p of ra.perms) if (!cur.perms.includes(p)) cur.perms.push(p);
        }
        for (const [jid, du] of sub.d) if (!dMap.has(jid)) dMap.set(jid, du);
      }
      roles.set(node.page_id, [...rMap.values()]
        .map(r => ({ ...r, perms: permOrder.filter(p => r.perms.includes(p)) }))
        .sort((a, b) => a.name.localeCompare(b.name)));
      duties.set(node.page_id, [...dMap.values()].sort((a, b) => a.name.localeCompare(b.name)));
      return { r: rMap, d: dMap };
    };
    tree.forEach(visit);
    return { roles, duties };
  }, [tree, rolesByPage, dutiesByPage]);

  const isFilterActive = filter.trim().length > 0;
  const isSortActive   = sort.sortKey != null;

  // Application id → active-locale label, for grid cells and sorting (§17.B4).
  const appLabels = useMemo(() => {
    const m: Record<number, string> = {};
    for (const a of apps) m[a.application_id] = a.label;
    return m;
  }, [apps]);
  const appLabelFor = useCallback(
    (id: number | null) => (id != null ? (appLabels[id] ?? '') : ''),
    [appLabels],
  );

  // §17.B5/B12: facet options + derived flags for the Active + Application filters.
  const facetOptions = useMemo(() => ({
    active: [
      { value: 'Y', label: t('common.boolean.active', 'Active') },
      { value: 'N', label: t('common.boolean.inactive', 'Inactive') },
    ],
    application: apps.map(a => ({ value: String(a.application_id), label: a.label })),
  }), [t, apps]);
  const facetsActive   = facets.active.length > 0 || facets.application.length > 0;
  const anyFilterActive = facetsActive || isFilterActive;
  const clearAll = () => { setFilter(''); setFacets({ active: [], application: [] }); };

  const flatRows = useMemo<FlatRow[]>(() => {
    // §17.B12: a node passes the Active facet when nothing is selected, or its
    // is_active matches one of the selected values.
    const matchesActive = (node: PageNode) =>
      facets.active.length === 0 || facets.active.includes(node.is_active);
    // Application facet — neutral by default; matches on the page's application_id.
    const matchesApp = (node: PageNode) =>
      facets.application.length === 0 ||
      (node.application_id != null && facets.application.includes(String(node.application_id)));
    const matchesFacets = (node: PageNode) => matchesActive(node) && matchesApp(node);

    // When a sort is active, flatten the tree (depth=0 for everything) and sort.
    // Drag-and-drop is disabled in that mode (and while filtering) since
    // visible order no longer matches the persisted parent/sort_order.
    if (isSortActive) {
      const all: FlatRow[] = [];
      const walk = (nodes: PageNode[]) => {
        for (const n of nodes) {
          if (matchesFacets(n)) all.push({ node: n, depth: 0, parentLabel: n.parent_page_id != null ? (labels[n.parent_page_id] ?? '') : '(root)' });
          walk(n.children);
        }
      };
      walk(tree);
      return sort.sorted(all, (a, b, key) => {
        switch (key) {
          case 'label':           return compareStrings(a.node.label, b.node.label);
          case 'internal_code':   return compareStrings(a.node.internal_code, b.node.internal_code);
          case 'route_path':      return compareStrings(a.node.route_path, b.node.route_path);
          case 'application_label': return compareStrings(appLabelFor(a.node.application_id), appLabelFor(b.node.application_id));
          case 'parent_label':    return compareStrings(a.parentLabel, b.parentLabel);
          case 'is_active':       return compareStrings(a.node.is_active, b.node.is_active);
          case 'is_user_managed': return compareStrings(a.node.is_user_managed, b.node.is_user_managed);
        }
      });
    }

    if (isFilterActive) {
      const q = filter.toLowerCase();
      const all: FlatRow[] = [];
      const walk = (nodes: PageNode[], depth: number, parentLabel: string) => {
        for (const n of nodes) {
          if (
            matchesFacets(n) &&
            (n.label.toLowerCase().includes(q) ||
             n.internal_code.toLowerCase().includes(q) ||
             n.route_path.toLowerCase().includes(q))
          ) all.push({ node: n, depth, parentLabel });
          walk(n.children, depth + 1, n.label);
        }
      };
      walk(tree, 0, '');
      return all;
    }
    const out: FlatRow[] = [];
    const walk = (nodes: PageNode[], depth: number, parentLabel: string) => {
      for (const n of nodes) {
        if (matchesFacets(n)) out.push({ node: n, depth, parentLabel });
        if (expanded.has(n.page_id) && n.children.length > 0) {
          walk(n.children, depth + 1, n.label);
        }
      }
    };
    walk(tree, 0, '');
    return out;
  }, [tree, expanded, filter, facets, isFilterActive, isSortActive, sort, labels, appLabelFor]);

  const editing = useMemo(
    () => rows.find(r => r.page_id === editingId) ?? null,
    [rows, editingId],
  );

  const hasChildren = (pageId: number) => rows.some(r => r.parent_page_id === pageId);

  const isDescendantOf = (candidate: number, ancestor: number): boolean => {
    let current = rows.find(r => r.page_id === candidate);
    while (current?.parent_page_id != null) {
      if (current.parent_page_id === ancestor) return true;
      current = rows.find(r => r.page_id === current?.parent_page_id);
    }
    return false;
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Related-children detail panel (roles/duties) - independent of the tree +/-.
  const toggleDetail = (id: number) => {
    setDetailExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const buildRelatedGroups = (node: PageNode): RelatedChildGroup[] => [
    {
      key: 'roles',
      table: 'csm_role_master', pkColumn: 'role_id',
      title: t('pages.list.related.roles', 'Roles & permissions'),
      badgeTitles: PERM_TITLES,
      items: (relatedByPage.roles.get(node.page_id) ?? []).map(r => ({ id: r.role_id, label: r.name, title: r.code, badges: r.perms })),
    },
    {
      key: 'jobs',
      table: 'csm_job_description', pkColumn: 'job_description_id',
      title: t('pages.list.related.job_descriptions', 'Job Descriptions'),
      items: (relatedByPage.duties.get(node.page_id) ?? []).map(d => ({ id: d.job_id, label: d.name, title: d.code })),
    },
    buildTranslationsGroup(tlByPage[node.page_id], t, activeLocale),
  ];

  const startCreate = (parent_id: number | null) => {
    setDraft({ parent_id });
    setEditingId(null);
  };

  const onDelete = async (row: PageRow) => {
    if (row.is_user_managed !== 'Y') {
      setError(t('pages.list.alerts.registry_only_delete'));
      return;
    }
    if (hasChildren(row.page_id)) {
      setError(t('pages.list.alerts.has_children'));
      return;
    }
    setDeleteTarget(row);
  };

  const confirmDelete = async (row: PageRow) => {
    const { error: e } = await routedDelete(session, 'csm_page_master', 'page_id', row.page_id);
    if (e) { setError(e.message); return; }
    setEditingId(null);
    setDeleteTarget(null);
    void reload();
  };

  // ------------------------------------------------------------------
  // PROMOTE / DEMOTE - shift a page one level in the hierarchy.
  //
  //   Promote (outdent): move the page out of its parent, becoming a
  //                       sibling of the old parent. New sort_order is
  //                       appended at the new level so the user sees it
  //                       at the bottom of its new sibling group.
  //
  //   Demote  (indent) : nest the page under the sibling that precedes it
  //                       at the same level. Disabled for the first
  //                       sibling because there's nothing to nest under.
  // ------------------------------------------------------------------

  const canPromote = (row: PageRow) => row.parent_page_id != null;

  const canDemote = (row: PageRow) => {
    const siblings = rows
      .filter(r => r.parent_page_id === row.parent_page_id)
      .sort((a, b) => a.sort_order - b.sort_order);
    const idx = siblings.findIndex(r => r.page_id === row.page_id);
    return idx > 0;
  };

  const promote = async (row: PageRow) => {
    if (!canPromote(row)) return;
    const currentParent = rows.find(r => r.page_id === row.parent_page_id);
    const newParentId = currentParent?.parent_page_id ?? null;
    const newSiblings = rows.filter(r =>
      r.parent_page_id === newParentId && r.page_id !== row.page_id);
    const newSort = newSiblings.length === 0
      ? SORT_STEP
      : Math.max(...newSiblings.map(s => s.sort_order)) + SORT_STEP;

    const { error: e } = await routedUpdate(session, 'csm_page_master', 'page_id', row.page_id, {
      parent_page_id: newParentId,
      sort_order: newSort,
    });
    if (e) { setError(e.message); return; }

    if (newParentId != null) {
      setExpanded(prev => new Set(prev).add(newParentId));
    }
    await reload();
  };

  const demote = async (row: PageRow) => {
    if (!canDemote(row)) return;
    const siblings = rows
      .filter(r => r.parent_page_id === row.parent_page_id)
      .sort((a, b) => a.sort_order - b.sort_order);
    const idx = siblings.findIndex(r => r.page_id === row.page_id);
    const prevSibling = siblings[idx - 1];

    const newParentId = prevSibling.page_id;
    const newSiblings = rows.filter(r => r.parent_page_id === newParentId);
    const newSort = newSiblings.length === 0
      ? SORT_STEP
      : Math.max(...newSiblings.map(s => s.sort_order)) + SORT_STEP;

    const { error: e } = await routedUpdate(session, 'csm_page_master', 'page_id', row.page_id, {
      parent_page_id: newParentId,
      sort_order: newSort,
    });
    if (e) { setError(e.message); return; }

    setExpanded(prev => new Set(prev).add(newParentId));
    await reload();
  };

  // ---------------------------------------------------------------------------
  // DRAG-AND-DROP
  // ---------------------------------------------------------------------------

  const onDragStart = (e: React.DragEvent<HTMLTableRowElement>, page_id: number) => {
    if (isFilterActive || isSortActive) { e.preventDefault(); return; }
    draggingIdRef.current = page_id;
    setDraggingId(page_id);
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(page_id)); } catch { /* noop */ }
  };

  const onDragOver = (e: React.DragEvent<HTMLTableRowElement>, page_id: number) => {
    const src = draggingIdRef.current;
    if (src == null) return;
    if (src === page_id) return;                          // can't drop on self
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const h = rect.height;
    let position: DropPosition;
    if (y < h * 0.25)      position = 'above';
    else if (y > h * 0.75) position = 'below';
    else                   position = 'into';

    // Reject reparenting onto a descendant.
    if (position === 'into' && isDescendantOf(page_id, src)) return;
    // Reject sibling-of-target if target's parent chain contains source.
    if (position !== 'into') {
      const targetRow = rows.find(r => r.page_id === page_id);
      if (targetRow?.parent_page_id != null
          && (targetRow.parent_page_id === src || isDescendantOf(targetRow.parent_page_id, src))) {
        return;
      }
    }

    setDropTarget(prev =>
      prev?.page_id === page_id && prev.position === position ? prev : { page_id, position },
    );
  };

  const onDragLeaveRow = (page_id: number) => {
    setDropTarget(prev => (prev?.page_id === page_id ? null : prev));
  };

  const onDragEnd = () => {
    draggingIdRef.current = null;
    setDraggingId(null);
    setDropTarget(null);
  };

  const onDrop = async (e: React.DragEvent<HTMLTableRowElement>) => {
    e.preventDefault();
    const src = draggingIdRef.current;
    const tgt = dropTarget;
    onDragEnd();
    if (src == null || !tgt) return;
    await applyDrop(src, tgt);
    await reload();
  };

  /**
   * Computes the new (parent_page_id, sibling order) and persists it.
   * For 'above' / 'below', renumbers all affected siblings sequentially with
   * SORT_STEP gaps so future fractional inserts have room.
   * For 'into', appends the source as the last child of the target.
   */
  const applyDrop = async (sourceId: number, target: DropTarget) => {
    const source = rows.find(r => r.page_id === sourceId);
    const tgtRow = rows.find(r => r.page_id === target.page_id);
    if (!source || !tgtRow) return;

    if (target.position === 'into') {
      const siblings = rows
        .filter(r => r.parent_page_id === tgtRow.page_id && r.page_id !== sourceId);
      const newSort = siblings.length === 0
        ? SORT_STEP
        : Math.max(...siblings.map(s => s.sort_order)) + SORT_STEP;

      const { error: e } = await routedUpdate(session, 'csm_page_master', 'page_id', sourceId, {
        parent_page_id: tgtRow.page_id,
        sort_order: newSort,
      });
      if (e) { setError(e.message); return; }

      // Auto-expand the new parent so the user sees the result.
      setExpanded(prev => new Set(prev).add(tgtRow.page_id));
      return;
    }

    // 'above' / 'below' - same parent as target, renumber siblings.
    const newParent = tgtRow.parent_page_id;
    const ordered = rows
      .filter(r => r.parent_page_id === newParent && r.page_id !== sourceId)
      .sort((a, b) => a.sort_order - b.sort_order);

    const tgtIdx = ordered.findIndex(s => s.page_id === target.page_id);
    if (tgtIdx === -1) return;
    const insertIdx = target.position === 'above' ? tgtIdx : tgtIdx + 1;
    const next = [...ordered.slice(0, insertIdx), source, ...ordered.slice(insertIdx)];

    // Persist sequentially - small N, fine without batching.
    for (let i = 0; i < next.length; i++) {
      const desiredSort = (i + 1) * SORT_STEP;
      const desiredParent = newParent;
      const row = next[i];
      // Skip no-op updates to keep the audit chain tight.
      if (row.parent_page_id === desiredParent && row.sort_order === desiredSort) continue;
      const { error: eU } = await routedUpdate(session, 'csm_page_master', 'page_id', row.page_id, {
        parent_page_id: desiredParent,
        sort_order: desiredSort,
      });
      if (eU) { setError(eU.message); return; }
    }
  };

  // CSS classes for drop indicators.
  const dropClass = (page_id: number): string => {
    if (!dropTarget || dropTarget.page_id !== page_id) return '';
    switch (dropTarget.position) {
      case 'above': return 'border-t-2 border-primary';
      case 'below': return 'border-b-2 border-primary';
      case 'into':  return 'bg-csm-info-soft outline outline-2 outline-blue-400';
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('pages.list.title', 'Pages')}
          description={t('pages.list.description', 'The navigable page hierarchy — reorder, reparent and edit menu entries.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              <button type="button" onClick={() => startCreate(null)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('pages.list.new_root')}
              </button>
            </>
          }
        />

        <StatStrip
          stats={[
            { value: rows.length, label: t('pages.stat.total', 'Pages') },
            { value: rows.filter(r => r.is_active === 'Y').length, label: t('pages.stat.active', 'Active') },
          ]}
        />

        {error && (
          <ErrorBanner
            message={t('pages.list.load_failed', "Couldn't complete the operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}

        {/* Content card — toolbar, hierarchical grid */}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                placeholder={t('pages.list.search_placeholder')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter
              label={t('common.boolean.active', 'Active')}
              options={facetOptions.active}
              selected={facets.active}
              onChange={v => setFacets(f => ({ ...f, active: v }))}
            />
            {apps.length > 0 && (
              <FacetFilter
                label={t('pages.list.filter.application', 'Application')}
                options={facetOptions.application}
                selected={facets.application}
                onChange={v => setFacets(f => ({ ...f, application: v }))}
              />
            )}
            <ExtensionFacets ext={ext} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <button
              type="button"
              disabled={!editing}
              onClick={() => editing && startCreate(editing.page_id)}
              className="h-8 rounded-csm-control border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt disabled:opacity-50"
            >{t('pages.list.new_child')}</button>
            <span className="ml-2 text-csm-xs text-csm-muted">
              {isFilterActive
                ? t('pages.list.drag_hint_filter')
                : isSortActive
                  ? t('pages.list.drag_hint_sort')
                  : t('pages.list.drag_hint_default')}
            </span>
            {isSortActive && (
              <button
                type="button"
                onClick={sort.clearSort}
                className="h-8 rounded-csm-control border border-csm-border bg-white px-2 text-csm-xs hover:bg-csm-surface-alt"
              >{t('pages.list.clear_sort')}</button>
            )}
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('pages.list.personalization_banner', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
        {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5" title={t('pages.list.related.header', 'Related roles & job descriptions')}></th>
                  <th className="w-6 border-b border-csm-border px-1 py-1.5"></th>
                  {visibleCols.map(col => (
                    <SortableHeader<PageSortKey> key={col.key}
                      sortKey={col.key as PageSortKey} label={col.header}
                      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                      style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('pages.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {flatRows.map(({ node, depth, parentLabel }) => {
                  const kids = node.children.length > 0;
                  const isOpen = expanded.has(node.page_id);
                  const isSelected = editingId === node.page_id;
                  const isDragging = draggingId === node.page_id;
                  // Every page can expand to audit its access: the roles &
                  // permissions (and job descriptions) it appears in — its own grants
                  // plus, for a menu group, the roles rolled up from the pages beneath
                  // it. Pages reached by no role show an explicit empty state rather
                  // than an inert control, so an admin can verify any page.
                  const canExpandDetail = true;
                  const isDetailOpen = detailExpanded.has(node.page_id);
                  return (
                    <Fragment key={node.page_id}>
                    <tr
                      draggable={!isFilterActive}
                      onDragStart={(e) => onDragStart(e, node.page_id)}
                      onDragOver={(e) => onDragOver(e, node.page_id)}
                      onDragLeave={() => onDragLeaveRow(node.page_id)}
                      onDrop={onDrop}
                      onDragEnd={onDragEnd}
                      onClick={canChange ? (() => { setEditingId(node.page_id); setDraft(null); }) : undefined}
                      className={
                        'cursor-pointer select-none ' +
                        (isSelected ? 'bg-csm-info-soft/40 ' : 'hover:bg-csm-surface-alt ') +
                        (isDragging ? 'opacity-40 ' : '') +
                        dropClass(node.page_id)
                      }
                    >
                      {/* Related-children disclosure (plus/minus) - reveals the side-by-side panel */}
                      <td
                        className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RelatedExpandButton
                          open={isDetailOpen}
                          hasChildren={canExpandDetail}
                          onToggle={() => toggleDetail(node.page_id)}
                          expandLabel={t('pages.list.related.expand_aria', 'Show the roles and permissions this page appears in')}
                          collapseLabel={t('pages.list.related.collapse_aria', 'Hide roles & permissions')}
                          tooltip={t('pages.list.related.tooltip', 'Show the roles and permissions this page appears in')}
                        />
                      </td>
                      <td
                        className="border-b border-csm-border-soft px-1 py-1.5 text-center text-csm-watermark"
                        title={t('pages.list.actions.drag_tooltip')}
                        style={{ cursor: isFilterActive ? 'not-allowed' : 'grab' }}
                      >
                        {/* simple grip glyph */}
                        <span aria-hidden>::</span>
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'label' && (
                            <span className="inline-flex items-center" style={{ paddingLeft: depth * 14 }}>
                              {kids && !isFilterActive ? (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleExpand(node.page_id); }}
                                  aria-label={isOpen ? t('pages.list.actions.collapse_aria') : t('pages.list.actions.expand_aria')}
                                  className="mr-1 inline-flex h-4 w-4 items-center justify-center text-csm-muted hover:text-csm-ink"
                                >{isOpen ? '-' : '+'}</button>
                              ) : (
                                <span className="mr-1 inline-block w-4 text-center text-csm-border-strong">·</span>
                              )}
                              <span>{node.label}</span>
                            </span>
                          )}
                          {col.key === 'internal_code' && (
                            <span className="font-mono text-csm-xs text-csm-ink-soft">{node.internal_code}</span>
                          )}
                          {col.key === 'route_path' && (
                            <span className="font-mono text-csm-xs text-csm-ink-soft">{node.route_path}</span>
                          )}
                          {col.key === 'application_label' && (
                            <span className="text-csm-ink-soft">{appLabelFor(node.application_id) || '—'}</span>
                          )}
                          {col.key === 'parent_label' && (
                            <span className="text-csm-ink-soft">
                              {parentLabel || (node.parent_page_id == null ? t('pages.list.parent_root') : labels[node.parent_page_id] ?? '')}
                            </span>
                          )}
                          {col.key === 'is_active' && (
                            <YesNoBadge value={node.is_active} yesLabel={t('pages.list.badge.active', 'Active')} noLabel={t('pages.list.badge.inactive', 'Inactive')} />
                          )}
                          {col.key === 'is_user_managed' && (
                            node.is_user_managed === 'Y' ? (
                              <span className="rounded border border-csm-info-border bg-csm-info-soft px-1.5 py-0.5 text-csm-2xs uppercase text-csm-info">{t('pages.list.source.user', 'User')}</span>
                            ) : (
                              <span className="rounded border border-csm-border bg-csm-surface-alt px-1.5 py-0.5 text-csm-2xs uppercase text-csm-ink-soft">{t('pages.list.source.code', 'Code')}</span>
                            )
                          )}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                        {canChange && (<button
                          onClick={(e) => { e.stopPropagation(); setEditingId(node.page_id); setDraft(null); }}
                          className="mr-1 inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft"
                        >{t('pages.list.actions.edit')}</button>)}
                        <span onClick={(e) => e.stopPropagation()}>
                          <RowActionsMenu
                            label={t('common.row_actions', 'Row actions')}
                            actions={[
                              { key: 'promote', label: t('pages.list.actions.promote'), onSelect: () => void promote(node), disabled: !canPromote(node) },
                              { key: 'demote', label: t('pages.list.actions.demote'), onSelect: () => void demote(node), disabled: !canDemote(node) },
                              { key: 'add_child', label: t('pages.list.actions.add_child'), onSelect: () => startCreate(node.page_id) },
                              { key: 'delete', label: t('pages.list.actions.delete'), onSelect: () => void onDelete(node), disabled: node.is_user_managed !== 'Y' || hasChildren(node.page_id), danger: true },
                            ]}
                          />
                        </span>
                      </td>
                    </tr>
                    {isDetailOpen && (
                      <tr>
                        <td
                          colSpan={visibleCols.length + 3}
                          className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* `session` is the other half of the drill-down gate:
                              RelatedChildrenPanel only makes a child clickable when it
                              has a session AND the group carries its identity. Without
                              it the table/pkColumn declared above are inert, and both
                              groups here resolve to registered editors (Role, Job
                              Description), each RBAC-gated by the CHILD's own page. */}
                          <RelatedChildrenPanel
                            groups={buildRelatedGroups(node)}
                            emptyLabel={t('pages.list.related.none', 'No roles or job descriptions have access to this page.')}
                            session={session}
                            onSaved={() => void reload()}
                          />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {flatRows.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 3} className="py-12 text-center">
                    <EmptyState
                      variant={filter ? 'no-match' : 'no-data'}
                      title={t('pages.list.empty_title', 'No pages found')}
                      description={filter ? t('pages.list.empty_filter_desc', 'No pages match "{{filter}}"', { filter }) : undefined}
                      action={!filter ? (
                        <button onClick={() => startCreate(null)} className="mt-2 rounded bg-primary px-3 py-1.5 text-csm-sm text-white hover:bg-primary-hover">
                          {t('pages.list.empty_new_root', '+ New Root Page')}
                        </button>
                      ) : undefined}
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
        )}
          </div>
        </ContentCard>
      </div>

      {(editing || draft) && (
        <PageEditModal
          session={session}
          row={editing}
          parentIfCreate={draft?.parent_id ?? null}
          allRows={rows}
          apps={apps}
          labels={labels}
          onClose={() => { setEditingId(null); setDraft(null); }}
          onSaved={(newId) => {
            setDraft(null);
            setEditingId(newId ?? null);
            void reload();
          }}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          title={t('pages.list.delete_confirm_title', 'Delete Page?')}
          message={t('pages.list.delete_confirm_message', 'Delete "{{code}}"? This cannot be undone.', { code: deleteTarget.internal_code })}
          onConfirm={async () => { await confirmDelete(deleteTarget); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// EDIT MODAL (no Sort Order field - drag-and-drop drives ordering)
// =============================================================================

interface PageEditModalProps {
  session: AuthSession;
  row: PageRow | null;
  parentIfCreate: number | null;
  allRows: PageRow[];
  apps: AppOption[];
  labels: Record<number, string>;
  onClose: () => void;
  onSaved: (newId: number | null) => void;
}

function PageEditModal({
  session, row, parentIfCreate, allRows, apps, labels, onClose, onSaved,
}: PageEditModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  const isCreate = row === null;
  const isRegistryRow = !isCreate && row?.is_user_managed === 'N';
  // A derived app's manifest-declared attributes for this record (§16). null on
  // create — the sister row is keyed by page_id, which does not exist yet.
  const extForm = useRecordExtensionForm(PAGE_CODE, row?.page_id ?? null);

  // New pages default to the flagged default application (else the first one).
  const defaultAppId = apps.find(a => a.is_default === 'Y')?.application_id ?? apps[0]?.application_id ?? null;
  const initialValues = {
    internalCode: row?.internal_code ?? '',
    routePath: row?.route_path ?? '',
    parentId: (isCreate ? parentIfCreate : (row?.parent_page_id ?? null)) as number | null,
    applicationId: (isCreate ? defaultAppId : (row?.application_id ?? defaultAppId)) as number | null,
    requiresSignoff: row?.requires_signoff === 'Y',
    signoffCount: row?.signoff_count ?? 0,
    isActive: row ? row.is_active === 'Y' : true,
  };

  const { values: form, setValues: setForm, isDirty, reset } = useDirtyForm(initialValues);
  const { layout } = useUldLayout('CSM.PAGE_MASTER.MODAL', PAGE_CORE_LAYOUT);
  const [showDiscard, setShowDiscard] = useState(false);
  const [internalCodeError, setInternalCodeError] = useState<string | null>(null);

  const [translations, setTranslations] = useState<TLRow[]>([]);
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState<string | null>(null);

  useEffect(() => { reset(initialValues); }, [row?.page_id]); // eslint-disable-line

  const handleClose = () => {
    if (isDirty) { setShowDiscard(true); return; }
    onClose();
  };

  useEffect(() => {
    if (isCreate || !row) {
      setTranslations([{ locale_code: 'EN_US', display_text: '', description: '' }]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from('csm_page_master_tl')
        .select('locale_code, display_text, description')
        .eq('page_id', row.page_id);
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      const list: TLRow[] = (data ?? []).map((t: TlRow) => ({
        locale_code: t.locale_code,
        display_text: t.display_text ?? '',
        description: t.description ?? '',
      }));
      setTranslations(list.length > 0 ? list : [
        { locale_code: 'EN_US', display_text: '', description: '' },
      ]);
    })();
    return () => { cancelled = true; };
  }, [isCreate, row?.page_id]); // eslint-disable-line

  const descendantIds = useMemo(() => {
    if (isCreate || !row) return new Set<number>();
    const desc = new Set<number>([row.page_id]);
    let added = true;
    while (added) {
      added = false;
      for (const r of allRows) {
        if (r.parent_page_id != null && desc.has(r.parent_page_id) && !desc.has(r.page_id)) {
          desc.add(r.page_id); added = true;
        }
      }
    }
    return desc;
  }, [isCreate, row, allRows]);

  const parentOptions = allRows
    // §17: parent lookup mirrors the list's Active-only default — no inactive parents.
    .filter(r => r.is_active === 'Y' && !descendantIds.has(r.page_id))
    .sort((a, b) => (labels[a.page_id] ?? a.internal_code).localeCompare(labels[b.page_id] ?? b.internal_code));

  const save = async () => {
    setBusy(true); setError(null);
    try {
      let pageId: number | null = row?.page_id ?? null;

      if (isCreate) {
        // Append to end of target parent's children.
        const siblings = allRows.filter(r => r.parent_page_id === form.parentId);
        const newSort  = siblings.length === 0
          ? 10
          : Math.max(...siblings.map(s => s.sort_order)) + 10;

        const { row_id, error: e } = await routedInsert(session, 'csm_page_master', {
          internal_code: form.internalCode,
          route_path: form.routePath,
          parent_page_id: form.parentId,
          application_id: form.applicationId,
          sort_order: newSort,
          requires_signoff: form.requiresSignoff ? 'Y' : 'N',
          signoff_count: form.signoffCount,
          is_active: form.isActive ? 'Y' : 'N',
          is_user_managed: 'Y',
        });
        if (e) throw e;
        pageId = row_id;
      } else {
        const patch: Record<string, unknown> = {
          parent_page_id: form.parentId,
          requires_signoff: form.requiresSignoff ? 'Y' : 'N',
          signoff_count: form.signoffCount,
          is_active: form.isActive ? 'Y' : 'N',
        };
        if (!isRegistryRow) {
          patch.internal_code = form.internalCode;
          patch.route_path    = form.routePath;
          // Application is code-controlled for registry rows (the sync re-asserts
          // it each login), so it is editable only on user-managed pages.
          patch.application_id = form.applicationId;
        }
        const { error: e } = await routedUpdate(session, 'csm_page_master', 'page_id', row?.page_id, patch);
        if (e) throw e;
      }

      for (const t of translations) {
        if (!t.locale_code || !t.display_text) continue;
        const { error: e } = await apiDb
          .from('csm_page_master_tl')
          .upsert({
            page_id: pageId,
            locale_code: t.locale_code,
            display_text: t.display_text,
            description: t.description || null,
          }, { onConflict: 'page_id,locale_code' });
        if (e) throw e;
      }

      // A derived app's extension row, after the host record (and its
      // translations) are safely saved (see use_record_extension_form for why
      // the two are sequential — the sister row is keyed by the host's pk).
      if (pageId != null) await extForm.save(session, pageId);

      onSaved(pageId);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <Modal
      title={isCreate ? t('pages.drawer.title_create') : t('pages.drawer.title_edit', { code: row?.internal_code ?? '' })}
      onClose={handleClose}
      width="xl"
      headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
      notice={isPersonalizing ? (
        <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
          {t('pages.drawer.personalization_banner', 'Toggle fields — hide · read-only · required. Click icon to save.')}
        </div>
      ) : undefined}
      footer={
        <>
          <button
            onClick={handleClose}
            className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft"
          >{t('common.cancel')}</button>
          <button
            onClick={() => void save()}
            disabled={busy || (isCreate && (!form.internalCode || !form.routePath))}
            className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60"
          >{busy ? t('common.saving') : t('common.save')}</button>
        </>
      }
    >
      <div>
          {isRegistryRow && (
            <div className="mb-3 rounded border border-csm-warning-border bg-csm-warning-soft px-2 py-1 text-csm-xs uppercase tracking-wider text-csm-warning">
              {t('pages.drawer.registry_banner')}
            </div>
          )}

          <div className="mb-4">
            <FieldRenderer
              fields={PAGE_FIELD_DEFS}
              layout={layout}
              columns={2}
              pageCode="CSM.PAGE_MASTER.MODAL"
              session={session}
              render={(f) => {
                if (f.key === 'internal_code') return (
                  <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('pages.drawer.field.internal_code')}>
                    <div>
                      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pages.drawer.field.internal_code')}</label>
                      <input
                        type="text"
                        value={form.internalCode}
                        disabled={!isCreate || isRegistryRow}
                        onChange={e => setForm({ ...form, internalCode: e.target.value.toUpperCase() })}
                        onBlur={() => {
                          if (isCreate && form.internalCode && !/^[A-Z][A-Z0-9_.]*$/.test(form.internalCode)) {
                            setInternalCodeError(t('pages.drawer.field.internal_code_format_error', 'Must start with a letter — uppercase letters, numbers, dots only.'));
                          } else {
                            setInternalCodeError(null);
                          }
                        }}
                        placeholder={t('pages.drawer.field.internal_code_placeholder')}
                        className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted"
                      />
                      {internalCodeError
                        ? <p className="text-csm-xs text-red-600 mt-1">{internalCodeError}</p>
                        : <p className="text-csm-xs text-csm-ink-soft mt-1">{t('pages.drawer.field.internal_code_hint', 'E.g. CSM.MANAGER — uppercase, dots only')}</p>
                      }
                    </div>
                  </PersonalizedField>
                );
                if (f.key === 'route_path') return (
                  <PersonalizedField componentKey={CK} fieldKey="route_path" label={t('pages.drawer.field.route_path')}>
                    <div>
                      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pages.drawer.field.route_path')}</label>
                      <input
                        type="text"
                        value={form.routePath}
                        disabled={isRegistryRow}
                        onChange={e => setForm({ ...form, routePath: e.target.value })}
                        placeholder={t('pages.drawer.field.route_path_placeholder')}
                        className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted"
                      />
                    </div>
                  </PersonalizedField>
                );
                if (f.key === 'application') return (
                  <PersonalizedField componentKey={CK} fieldKey="application" label={t('pages.drawer.field.application', 'Application')}>
                    <div>
                      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pages.drawer.field.application', 'Application')}</label>
                      {isRegistryRow ? (
                        <div className="flex h-8 items-center rounded border border-csm-border bg-csm-surface-alt px-2 text-csm-sm text-csm-muted">
                          {apps.find(a => a.application_id === form.applicationId)?.label
                            ?? t('pages.drawer.field.application_base', 'Base Application')}
                        </div>
                      ) : (
                        <FilteredModalSingleSelect
                          label={t('pages.drawer.field.application', 'Application')}
                          options={apps.map(a => ({ internal_code: a.internal_code, display_text: a.label }))}
                          selectedCode={apps.find(a => a.application_id === form.applicationId)?.internal_code ?? null}
                          onChange={(code) => {
                            const a = apps.find(app => app.internal_code === code);
                            setForm({ ...form, applicationId: a?.application_id ?? null });
                          }}
                          placeholder={t('pages.drawer.field.application_placeholder', 'Select application…')}
                        />
                      )}
                      <div className="mt-1 text-csm-2xs text-csm-muted">
                        {isRegistryRow
                          ? t('pages.drawer.field.application_registry_hint', 'Code-managed pages belong to the base application.')
                          : t('pages.drawer.field.application_hint', 'The application this page belongs to.')}
                      </div>
                    </div>
                  </PersonalizedField>
                );
                if (f.key === 'parent_page') return (
                  <PersonalizedField componentKey={CK} fieldKey="parent_page" label={t('pages.drawer.field.parent')}>
                    <div>
                      <FilteredModalSingleSelect
                        label={t('pages.drawer.field.parent')}
                        options={parentOptions.map(p => ({
                          internal_code: p.internal_code,
                          display_text: labels[p.page_id] ?? p.internal_code,
                        }))}
                        selectedCode={
                          form.parentId == null
                            ? null
                            : parentOptions.find(p => p.page_id === form.parentId)?.internal_code ?? null
                        }
                        onChange={(code) => {
                          if (code === null) setForm({ ...form, parentId: null });
                          else {
                            const p = parentOptions.find(pp => pp.internal_code === code);
                            setForm({ ...form, parentId: p?.page_id ?? null });
                          }
                        }}
                        placeholder={t('pages.drawer.field.parent_placeholder')}
                        clearLabel={t('pages.drawer.field.parent_clear_label')}
                        allowClear
                      />
                      <div className="mt-1 text-csm-2xs text-csm-muted">
                        {t('pages.drawer.field.parent_hint')}
                      </div>
                    </div>
                  </PersonalizedField>
                );
                if (f.key === 'is_active') return (
                  <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('pages.drawer.field.active')}>
                    <div>
                      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pages.drawer.field.active')}</label>
                      <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
                        <input
                          type="checkbox"
                          checked={form.isActive}
                          onChange={e => setForm({ ...form, isActive: e.target.checked })}
                          className="h-4 w-4 accent-gray-700"
                        />
                        {form.isActive ? t('common.yes') : t('common.no')}
                      </label>
                    </div>
                  </PersonalizedField>
                );
                if (f.key === 'requires_signoff') return (
                  <PersonalizedField componentKey={CK} fieldKey="requires_signoff" label={t('pages.drawer.field.requires_signoff')}>
                    <div>
                      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pages.drawer.field.requires_signoff')}</label>
                      <label className="flex h-8 items-center gap-2 text-csm-sm text-csm-ink-soft">
                        <input
                          type="checkbox"
                          checked={form.requiresSignoff}
                          onChange={e => setForm({ ...form, requiresSignoff: e.target.checked })}
                          className="h-4 w-4 accent-gray-700"
                        />
                        {form.requiresSignoff ? t('common.yes') : t('common.no')}
                      </label>
                    </div>
                  </PersonalizedField>
                );
                if (f.key === 'signoff_count') return (
                  <PersonalizedField componentKey={CK} fieldKey="signoff_count" label={t('pages.drawer.field.signoff_count')}>
                    <div>
                      <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('pages.drawer.field.signoff_count')}</label>
                      <input
                        type="number"
                        min={0}
                        value={form.signoffCount}
                        onChange={e => setForm({ ...form, signoffCount: Math.max(0, Number(e.target.value)) })}
                        disabled={!form.requiresSignoff}
                        className="h-8 w-full rounded border border-csm-border px-2 text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt"
                      />
                    </div>
                  </PersonalizedField>
                );
                return null;
              }}
            />
          </div>

          {/* Translations */}
          <PersonalizedField componentKey={CK} fieldKey="translations" label={t('pages.drawer.translations.label')}>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-csm-sm font-medium text-csm-ink-soft">{t('pages.drawer.translations.label')}</label>
            <button
              type="button"
              onClick={() => setTranslations(prev => [...prev, { locale_code: '', display_text: '', description: '' }])}
              className="text-csm-xs text-csm-ink-soft hover:underline"
            >{t('pages.drawer.translations.add_locale')}</button>
          </div>
          <div className="overflow-hidden rounded border border-csm-border">
            <table className="w-full text-csm-sm">
              <thead className="bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="px-2 py-1 text-left">{t('pages.drawer.translations.columns.locale')}</th>
                  <th className="px-2 py-1 text-left">{t('pages.drawer.translations.columns.display_text')}</th>
                  <th className="px-2 py-1 text-left">{t('pages.drawer.translations.columns.description')}</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {translations.map((tl, i) => (
                  <tr key={i} className="border-t border-csm-border-soft">
                    <td className="px-1 py-1">
                      <input
                        value={tl.locale_code}
                        onChange={e => {
                          const v = e.target.value.toUpperCase();
                          setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, locale_code: v } : row));
                        }}
                        placeholder={t('pages.drawer.translations.locale_placeholder')}
                        className="h-6 w-20 rounded border border-csm-border px-1 font-mono text-csm-xs"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        value={tl.display_text}
                        onChange={e => {
                          const v = e.target.value;
                          setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, display_text: v } : row));
                        }}
                        className="h-6 w-full rounded border border-csm-border px-1 text-csm-sm"
                      />
                    </td>
                    <td className="px-1 py-1">
                      <input
                        value={tl.description}
                        onChange={e => {
                          const v = e.target.value;
                          setTranslations(prev => prev.map((row, idx) => idx === i ? { ...row, description: v } : row));
                        }}
                        className="h-6 w-full rounded border border-csm-border px-1 text-csm-sm"
                      />
                    </td>
                    <td className="px-1 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => setTranslations(prev => prev.filter((_, idx) => idx !== i))}
                        className="text-csm-watermark hover:text-csm-danger"
                        aria-label={t('pages.drawer.translations.remove_aria')}
                      >x</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </PersonalizedField>

          {/* A derived app's own page attributes (src/app/extensions) — renders
              nothing when no extension is registered. */}
          <RecordExtensionFields form={extForm} componentKey={CK} />

          {error && (
            <div className="mt-3">
              <ErrorBanner
                message={t('pages.drawer.save_failed', "Couldn't save the page.")}
                detail={error}
                details_label={t('common.details', 'Details')}
              />
            </div>
          )}
      </div>
    </Modal>

    {showDiscard && (
      <ConfirmModal
        title={t('pages.drawer.discard_title', 'Discard changes?')}
        message={t('pages.drawer.discard_message', 'You have unsaved changes that will be lost.')}
        confirmLabel={t('pages.drawer.discard_confirm', 'Discard')}
        cancelLabel={t('pages.drawer.discard_cancel', 'Keep editing')}
        danger={false}
        onConfirm={() => { reset(); onClose(); }}
        onCancel={() => setShowDiscard(false)}
      />
    )}
    </>
  );
}

// ---------------------------------------------------------------------------
// §17.B13 drill-down — this page OWNS the page-master editor and registers it
// for other pages' related-children panels (module-load side effect; see
// RoleListPage for the rationale).
//
// PageEditModal is the heaviest of the editors: besides the row it needs the
// WHOLE page catalog (the parent picker is a tree over every page), the
// application list, and the id -> label map. All three come from the same reads
// the list performs, so the adapter repeats them rather than approximating —
// a truncated catalog would silently drop valid parents from the picker.
// ---------------------------------------------------------------------------
function PageChildEditor({ session, pk, onClose, onSaved }: ChildEditorProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [rows, setRows] = useState<PageRow[]>([]);
  const [apps, setApps] = useState<AppOption[]>([]);
  const [labels, setLabels] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const locale = activeLocale;
        type RawPage = PageRow & { csm_page_master_tl?: { locale_code: string; display_text: string }[] };
        // Range-paged for the same reason the list does it: the catalog can
        // exceed PostgREST's row cap, and a partial tree is worse than none.
        const data = await fetchAllRows<RawPage>((from, to) => apiDb
          .from('csm_page_master')
          .select(`
            page_id, internal_code, route_path, parent_page_id, application_id, sort_order,
            requires_signoff, signoff_count, is_active, is_user_managed,
            csm_page_master_tl ( locale_code, display_text )
          `)
          .order('sort_order').order('page_id').range(from, to));
        if (cancelled) return;

        setRows(data.map((p: RawPage) => ({
          page_id: p.page_id,
          internal_code: p.internal_code,
          route_path: p.route_path ?? '',   // null for non-navigable rows
          parent_page_id: p.parent_page_id,
          application_id: p.application_id,
          sort_order: p.sort_order,
          requires_signoff: p.requires_signoff,
          signoff_count: p.signoff_count,
          is_active: p.is_active,
          is_user_managed: p.is_user_managed,
        })));

        const lab: Record<number, string> = {};
        for (const p of data) {
          const tls = p.csm_page_master_tl ?? [];
          lab[p.page_id] = tls.find(x => x.locale_code === locale)?.display_text
            ?? tls.find(x => x.locale_code === 'EN_US')?.display_text
            ?? tls[0]?.display_text ?? p.internal_code;
        }
        setLabels(lab);

        const { data: appData } = await apiDb
          .from('csm_application_master')
          .select('application_id, internal_code, is_default, csm_application_master_tl ( locale_code, display_text )')
          .order('internal_code');
        if (cancelled) return;
        type RawApp = { application_id: number; internal_code: string; is_default: 'Y' | 'N'; csm_application_master_tl?: { locale_code: string; display_text: string }[] };
        setApps(((appData ?? []) as RawApp[]).map(a => {
          const tls = a.csm_application_master_tl ?? [];
          return {
            application_id: a.application_id,
            internal_code: a.internal_code,
            is_default: a.is_default,
            label: tls.find(x => x.locale_code === locale)?.display_text
              ?? tls.find(x => x.locale_code === 'EN_US')?.display_text
              ?? a.internal_code,
          };
        }));
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeLocale]);

  const row = rows.find(r => String(r.page_id) === String(pk)) ?? null;

  if (busy || error || !row) {
    return (
      <Modal title={t('pages.edit_title', 'Page')} onClose={onClose} width="lg">
        <div className={'px-1 py-3 text-csm-sm ' + (error ? 'text-csm-danger' : 'text-csm-muted')}>
          {error ?? (busy ? t('common.loading', 'Loading…') : t('common.not_found', 'Record not found.'))}
        </div>
      </Modal>
    );
  }
  return (
    <PageEditModal
      session={session} row={row} parentIfCreate={null}
      allRows={rows} apps={apps} labels={labels}
      onClose={onClose}
      /* PageEditModal reports the new id; the child-editor contract does not. */
      onSaved={() => onSaved()}
    />
  );
}

registerChildEditor('csm_page_master', {
  pageCode: PAGE_CODE,
  title: 'Page',
  Editor: PageChildEditor,
});
